// Object-driven sidebar panels for the Projects app.
//
// Every Projects hover panel is DATA — pure JSON interpreted by the engine in
// ./engine. There is no bespoke React here and none should be added: if a look
// or behaviour can't be expressed, extend the engine's vocabulary once (schema
// + engine) and select it from JSON, so every app gets it too.
//
// Panel `id` must match the `panelId` on the matching rail item in ../apps.ts.

import { API_ENDPOINTS } from '@clarix/utils';
import { P } from '@clarix/rbac';
import {
  ACTIVITY_CATEGORY_TABS,
  SIDEBAR_SCHEMA_VERSION,
  type CountRequest,
  type PanelConfig,
  type RowConfig,
  type SectionConfig,
  type SidebarConfig,
} from '@clarix/shared-types';
import { SETTING_SECTIONS, SETTING_TABS } from '../settings-tabs';
import { iconNameOf } from './engine/icons';

/* ────────────────────────── Projects ────────────────────────── */

const projectsPanel: PanelConfig = {
  id: 'projects',
  title: 'Projects',
  // Both page links need the all-projects permission — resolved at ORG level so
  // a single project-scoped grant can't reveal the whole list.
  headerRows: [
    {
      id: 'all-projects',
      label: 'All Projects',
      icon: 'FolderKanban',
      to: '/projects/list',
      exact: true,
      externalLinkIcon: 'strong',
      visibleWhen: { permission: P.PROJECTS_VIEW_ALL, base: true },
    },
    {
      // The tree below excludes archived projects (the server omits them from
      // /projects by default), so this is the only route back to them — and to
      // restoring one.
      id: 'archived-projects',
      label: 'Archived Projects',
      icon: 'Archive',
      to: '/projects/archived',
      exact: true,
      externalLinkIcon: 'strong',
      visibleWhen: { permission: P.PROJECTS_VIEW_ALL, base: true },
    },
  ],
  search: { placeholder: 'Search projects...' },
  sections: [
    {
      id: 'projects-tree',
      tree: true,
      loadingVariant: 'text',
      searchEmptyText: 'No matching projects',
      rowsSource: { provider: 'projectsTree' },
    },
  ],
};

/* ────────────────────────── Review timesheets ────────────────────────── */

const timesheetReviewPanel: PanelConfig = {
  id: 'timesheet',
  title: 'Review Timesheets',
  headerRows: [
    {
      id: 'review-all',
      label: 'Review All',
      icon: 'ClipboardCheck',
      to: '/projects/timesheets/review',
      // Reading a single project or the general view is not "review all".
      activeExtra: { paramAbsent: ['project'], paramNot: { key: 'view', value: 'general' } },
    },
    {
      id: 'review-general',
      label: 'Team / General',
      icon: 'Users',
      to: '/projects/timesheets/review?view=general',
    },
  ],
  sections: [
    {
      id: 'review-projects',
      label: 'Projects',
      divider: true,
      hideHeaderUntilData: true,
      errorText: 'Failed to load',
      rowsSource: { provider: 'timesheetReview' },
      toggleFilter: {
        label: 'My projects',
        ariaLabel: 'Show only projects where you have a role (PM, TL, …)',
        storageKey: 'clarix.timesheetReview.myProjectsOnly',
        field: 'mine',
        // Default ON: the panel opens scoped to the user's own projects.
        defaultOn: true,
      },
    },
  ],
};

/* ────────────────────────── Reports ────────────────────────── */

const reportScope = (
  id: string,
  label: string,
  icon: string,
  permission: (typeof P)[keyof typeof P],
  // Project-scoped reports only have data when some project grants the key.
  anyProject = false,
): RowConfig => ({
  id,
  label,
  icon,
  toParam: { pathname: '/projects/timesheets/reports', param: 'scope', value: id },
  // My Report lives on its own route but is the same scope switch.
  matchPaths: ['/projects/my-report'],
  visibleWhen: anyProject ? { permission, anyProject: true } : { permission },
});

const reportsPanel: PanelConfig = {
  id: 'reports',
  title: 'Reports',
  // No ?scope= in the URL means the page shows the first scope the user can see.
  defaultActiveFirst: true,
  sections: [
    {
      id: 'reports',
      rows: [
        reportScope('mine', 'My Report', 'Clock', P.MY_REPORT_VIEW),
        reportScope('person', 'By person', 'Users', P.REPORT_TIMESHEET_VIEW, true),
        reportScope('project', 'By project', 'FolderKanban', P.REPORT_TIMESHEET_VIEW, true),
        reportScope('team', 'My Team Report', 'UsersRound', P.MY_TEAM_REPORT_VIEW, true),
      ],
    },
  ],
};

/* ────────────────────────── Activity ────────────────────────── */

const ACTIVITY_TAB_ICON: Record<string, string> = {
  all: 'LayoutList',
  time_tracking: 'Clock',
  task_action: 'ClipboardList',
  comment: 'MessageSquare',
  status_change: 'CheckCircle2',
};

const activityPanel: PanelConfig = {
  id: 'activity',
  title: 'Activity',
  headerRows: [
    {
      id: 'my-activity',
      label: 'My Activity',
      icon: 'Activity',
      to: '/projects/activity',
      externalLinkIcon: 'subtle',
    },
  ],
  sections: [
    {
      id: 'categories',
      label: 'Categories',
      divider: true,
      rows: ACTIVITY_CATEGORY_TABS.map<RowConfig>((tab) => ({
        id: tab.value,
        label: tab.label,
        icon: ACTIVITY_TAB_ICON[tab.value],
        toParam: {
          pathname: '/projects/activity',
          param: 'tab',
          value: tab.value,
          isDefault: tab.value === 'all',
        },
      })),
    },
  ],
};

/* ── Spark: no panel. ───────────────────────────────────────────────────────
   The board moved to the platform console in 2026-08, taking the feed, the
   admin dashboards and `GET /sparks` with it. This panel outlived them and kept
   calling that endpoint, which is what rendered "Failed to load" under a rail
   item that led nowhere. Raising a spark is a top-bar action now — no rail
   entry, no panel, no route. */

/* ────────────────────────── Users ────────────────────────── */

/**
 * True per-status counts, read exactly like the Users page tabs (one
 * `page_size: 1` request per status → `pagination.total`), so the sidebar can
 * never disagree with them. "All" is the sum of the three status tabs.
 */
const userCount = (status: string): CountRequest => ({
  api: {
    endpoint: API_ENDPOINTS.USERS,
    params: { status, include_invites: true, page: 1, page_size: 1 },
  },
  valuePath: 'pagination.total',
});

const usersPanel: PanelConfig = {
  id: 'users',
  title: 'Users',
  sections: [
    {
      id: 'users',
      errorText: 'Failed to load',
      rows: [
        {
          id: 'all',
          label: 'All Users',
          icon: 'Users',
          toParam: { pathname: '/projects/users', param: 'status', value: 'all', isDefault: true },
          badge: {
            kind: 'count',
            tone: 'gray',
            sum: [userCount('active'), userCount('deactivated'), userCount('invited')],
          },
        },
        {
          id: 'active',
          label: 'Active',
          icon: 'UserCheck',
          iconTone: 'emerald',
          toParam: { pathname: '/projects/users', param: 'status', value: 'USER_ACTIVE' },
          badge: { kind: 'count', tone: 'emerald', count: userCount('active') },
        },
        {
          id: 'deactivated',
          label: 'Deactivated',
          icon: 'UserX',
          toParam: { pathname: '/projects/users', param: 'status', value: 'USER_DEACTIVE' },
          badge: { kind: 'count', tone: 'gray', count: userCount('deactivated') },
        },
      ],
    },
  ],
};

/* ────────────────────────── Settings ────────────────────────── */

/**
 * Grouped settings tabs, generated from the SETTING_TABS constant so the panel
 * and the page can't drift. A tab is visible when the user holds its own
 * permission OR the umbrella settings key.
 */
const settingsPanel: PanelConfig = {
  id: 'settings',
  title: 'Settings',
  search: { placeholder: 'Search settings...' },
  defaultActiveFirst: true,
  sections: SETTING_SECTIONS.map<SectionConfig>((section) => ({
    id: section,
    label: section,
    rows: SETTING_TABS.filter((t) => t.section === section).map<RowConfig>((tab) => ({
      id: tab.id,
      label: tab.label,
      icon: iconNameOf(tab.icon),
      toParam: { pathname: '/projects/settings', param: 'tab', value: tab.id },
      visibleWhen: tab.requiredPermission
        ? {
            permission: [tab.requiredPermission, P.SETTINGS_VIEW],
            permissionAny: true,
            // Settings deliberately shows its tabs while the matrix hydrates and
            // lets the page re-gate — hiding them all would flash an empty panel
            // on every open.
            unloadedVisible: true,
          }
        : undefined,
    })),
  })),
};

/* ────────────────────────── Platform health ────────────────────────── */

const healthRow = (id: string, label: string, icon: string): RowConfig => ({
  id,
  label,
  icon,
  toParam: { pathname: '/projects/platform-health', param: 'project', value: id },
  badge: {
    kind: 'traffic-light',
    thresholds: [1, 10],
    count: {
      api: {
        endpoint: API_ENDPOINTS.MONITORING_ERRORS,
        service: 'api',
        params: { query: 'is:unresolved', sort: 'freq', limit: 50, project: id },
      },
      // The monitoring payload IS the array of issues.
      lengthPath: '',
    },
  },
});

const healthPanel: PanelConfig = {
  id: 'health',
  title: 'Platform Health',
  headerRows: [
    {
      id: 'platform-health',
      label: 'Platform Health',
      icon: 'Shield',
      to: '/projects/platform-health',
      exact: true,
      externalLinkIcon: 'subtle',
    },
  ],
  sections: [
    {
      id: 'issues',
      label: 'Issues',
      divider: true,
      errorText: 'Failed to load',
      rows: [
        healthRow('frontend', 'Frontend', 'Monitor'),
        healthRow('backend', 'Backend', 'Server'),
        healthRow('ai', 'AI', 'Bot'),
      ],
    },
  ],
};

export const projectsSidebar: SidebarConfig = {
  version: SIDEBAR_SCHEMA_VERSION,
  // Projects is the only app whose users run the desktop time Tracker, so it is
  // the only rail that offers the download. Every other slot is left to its
  // default (shown).
  utility: { trackerButton: true },
  panels: [
    projectsPanel,
    timesheetReviewPanel,
    reportsPanel,
    activityPanel,
    usersPanel,
    settingsPanel,
    healthPanel,
  ],
};
